import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { Box, cssVar, HStack, IconButton } from '@cad-arc';
import { htmlToText } from 'html-to-text';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import React, { memo, useCallback, useRef, useState } from 'react';

import {
    CommandListenersPlugin,
    ContentEditable,
    FlagNodes,
    FlagPlugin,
    $generateHtmlFromNodes,
    HistoryPlugin,
    LexicalComposer,
    LexicalErrorBoundary,
    OnChangePlugin,
    PlainTextPlugin,
} from '../../common/components/lexical';
import { AdditionalInfoView } from '../types';
import { MultiLineDetectorPlugin } from './multi-line-detector-plugin';
import { TabCyclePlugin } from './tab-cycle-plugin';
import { handleArrowKeyButtonNav, isKeyboardActivatedClick } from './narrative-action-menu';

export interface NarrativeEditRowProps {
    info: AdditionalInfoView;
    agencyEventId: number;
    isFirstResponder?: boolean;
    /** `isKeyboardActivated` — true for Enter-in-editor or a keyboard-activated
     *  (detail === 0) Save-button press, false for a real mouse/touch click on
     *  Save. Forwarded to handleEditSave so it can choose stay-selected vs
     *  deselect — see use-narrative-row-actions.ts. */
    onSave: (updatedInfo: AdditionalInfoView, isKeyboardActivated: boolean) => void;
    /** Same `isKeyboardActivated` contract as onSave above. */
    onCancel: (isKeyboardActivated: boolean) => void;
    isSaving?: boolean;
}

export const NarrativeEditRow = memo(function NarrativeEditRow({
    info,
    isFirstResponder = false,
    onSave,
    onCancel,
    isSaving = false,
}: NarrativeEditRowProps) {
    const initialText = info.text ?? '';
    const textRef = useRef(initialText);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const [isMultiLine, setIsMultiLine] = useState(() => initialText.includes('\n'));
    const [hasChanged, setHasChanged] = useState(false);

    const handleChange = useCallback(
        (editorState, editor) => {
            editorState.read(() => {
                const html = $generateHtmlFromNodes(editor, null);
                textRef.current = htmlToText(html, {
                    preserveNewlines: true,
                    whitespaceCharacters: '',
                });
                setHasChanged(textRef.current !== initialText);
            });
        },
        [initialText]
    );

    const handleSave = useCallback(
        (isKeyboardActivated: boolean) => {
            if (!hasChanged) return;
            onSave({ ...info, text: textRef.current }, isKeyboardActivated);
        },
        [hasChanged, info, onSave]
    );

    const handleEnterDown = useCallback(() => handleSave(true), [handleSave]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCancel(true);
                return;
            }
            handleArrowKeyButtonNav(e, e.currentTarget);
        },
        [onCancel]
    );

    const buttonSize = isFirstResponder ? 'lg' : 'sm';
    const fontSize = isFirstResponder ? '18px' : '14px';

    return (
        <Box
            w="100%"
            paddingY="var(--arc-space-1)"
            paddingRight="var(--arc-space-1)"
            position="relative"
            data-fr={isFirstResponder}
            onKeyDown={handleKeyDown}
        >
            <LexicalComposer
                initialConfig={{
                    namespace: 'narrative-edit',
                    nodes: [...FlagNodes],
                    onError: () => {},
                    editorState: () => {
                        const root = $getRoot();
                        const paragraph = $createParagraphNode();
                        paragraph.append($createTextNode(initialText));
                        root.append(paragraph);
                    },
                }}
            >
                <PlainTextPlugin
                    contentEditable={
                        <ContentEditable
                            style={{
                                backgroundColor: 'var(--arc-colors-brand-content)',
                                color: 'var(--arc-colors-text-primary)',
                                fontSize,
                                width: '100%',
                                outline: 'none',
                                padding: isFirstResponder
                                    ? 'var(--arc-space-2) 100px var(--arc-space-2) var(--arc-space-2)'
                                    : 'var(--arc-space-2) 65px var(--arc-space-2) var(--arc-space-2)',
                                minHeight: isFirstResponder
                                    ? 'var(--arc-sizes-control-xl)'
                                    : undefined,
                                borderRadius: 'var(--arc-radii-md)',
                                border: '1px solid var(--arc-colors-border-default)',
                                maxHeight: 'var(--arc-space-60)',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                            }}
                            data-testid="NARRATIVE_EDIT_TEXTAREA"
                        />
                    }
                    placeholder={null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <AutoFocusPlugin />
                <OnChangePlugin onChange={handleChange} />
                <CommandListenersPlugin onEnterDown={handleEnterDown} />
                <MultiLineDetectorPlugin fontSize={fontSize} onMultiLineChange={setIsMultiLine} />
                <TabCyclePlugin cancelButtonRef={cancelButtonRef} saveButtonRef={saveButtonRef} />
                <FlagPlugin />
                <HistoryPlugin />
            </LexicalComposer>
            <HStack
                position="absolute"
                {...(isMultiLine
                    ? { bottom: 'var(--arc-space-4)' }
                    : { top: '50%', transform: 'translateY(-50%)' })}
                right="var(--arc-space-4)"
                zIndex={cssVar('arc.zIndices.dropdown')}
                spacing="var(--arc-space-1)"
            >
                <IconButton
                    ref={cancelButtonRef}
                    variant="outline"
                    size={buttonSize}
                    icon="CloseX"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        const isKeyboardActivated = isKeyboardActivatedClick(event);
                        // A real mouse/touch click should still bubble to the
                        // row's own onClick={handleRowDeselect} — clicking
                        // anywhere, including this button, clears selection
                        // (handleEditCancel's own isKeyboardActivated branch
                        // also deselects explicitly for this case, so the
                        // bubble is redundant-but-harmless here). A
                        // keyboard-synthesized click must NOT reach the row's
                        // onClick: handleEditCancel separately returns real
                        // DOM focus to the row (re-selecting it), and without
                        // stopping propagation here that reselect would be
                        // immediately undone by the same click finishing its
                        // bubble up to the row.
                        if (isKeyboardActivated) {
                            event.stopPropagation();
                        }
                        onCancel(isKeyboardActivated);
                    }}
                    isDisabled={isSaving}
                    data-testid="NARRATIVE_EDIT_CANCEL"
                    aria-label="Cancel edit"
                />
                <IconButton
                    ref={saveButtonRef}
                    variant="solid"
                    size={buttonSize}
                    icon="Check"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        // See the Cancel button's onClick above — same fix,
                        // for the same reason. (handleEditSave's async gap
                        // means the row's own onClick bubble would otherwise
                        // resolve BEFORE the save completes regardless of
                        // modality, which is exactly why the isKeyboardActivated
                        // branch in handleEditSave decides the final outcome
                        // explicitly instead of relying on that timing.)
                        const isKeyboardActivated = isKeyboardActivatedClick(event);
                        if (isKeyboardActivated) {
                            event.stopPropagation();
                        }
                        handleSave(isKeyboardActivated);
                    }}
                    isLoading={isSaving}
                    isDisabled={!hasChanged}
                    data-testid="NARRATIVE_EDIT_SAVE"
                    aria-label="Save edit"
                />
            </HStack>
        </Box>
    );
});
